import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { newGroupId } from '../editor/arrange';
import { ALL_SHAPES, SHAPE_CATS, SHAPE_TEXT_SIZE, SHAPE_TEXT_WIDTH, shapeById, shapeSvg, shapeWords, type ShapeDef } from '../editor/shapeLib';
import type { GfxItem } from '../graphics/graphics';
import { isThaiText, type ThaiTextbox } from '../thai/ThaiTextbox';
import { $ } from './dom';
import { makeText, SHAPE_FILL, shapeBase } from './objects';
import { closeSheets } from './sheets';
import { toast } from './toast';

/*
 * องค์ประกอบ → รูปทรง (choices 25 ต.ค. 2569: ก หมวด + "ดูทั้งหมด" · ก สีพื้น + เส้นขอบ + พิมพ์ข้อความในรูปทรง · ก 10 หมวด):
 *   a shape     one Fabric path (`shapeId` = which one, `shapeKey` = this one) — its colour and outline in the toolbar
 *               like the other shapes, vector in print PDFs
 *   its text    a double tap on it (or the chip "ข้อความ") types in it: a text box in the middle of it, in its group
 *               (`groupId`, they move together), linked by `shapeLink` = the shape's `shapeKey`; when the shape alone is
 *               made bigger/smaller or moved, its text follows
 */
export interface ShapeHooks {
  /** a Thai reason when the page cannot be changed now (someone else's work, a page still loading), else null */
  cannotEdit: () => string | null;
}

type Shape = fabric.Path & { shapeId?: string; shapeKey?: string; groupId?: string; locked?: boolean };
type ShapeText = ThaiTextbox & { shapeLink?: string; groupId?: string };
const PLACE = 0.3;   // a new shape's longer side: this part of the page's shorter side
const DOUBLE_MS = 450, DOUBLE_PX = 14;

const svgUrl = (s: ShapeDef, color: string) => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(shapeSvg(s, color));
const newKey = () => 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
/** the words on a shape: dark on a light shape, white on a dark one */
function inkOn(fill: unknown): string {
  const c = new fabric.Color(typeof fill === 'string' ? fill : '#6b3fd9').getSource();
  const lum = (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255;
  return lum > 0.6 ? '#1d1a2b' : '#ffffff';
}

export function initShapePanel(ed: Editor, history: History, h: ShapeHooks) {
  const { canvas } = ed;

  function tile(s: ShapeDef): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button'; b.className = 'shapetile';
    b.setAttribute('aria-label', s.th);
    b.innerHTML = shapeSvg(s, 'currentColor');
    b.onclick = () => insert(s.id);
    return b;
  }

  /* the categories: a short row each, "ดูทั้งหมด" opens all of one */
  const box = $('shapeCats');
  for (const c of SHAPE_CATS) {
    const sec = document.createElement('div'); sec.className = 'shapecat';
    const head = document.createElement('div'); head.className = 'shapehead';
    const name = document.createElement('b'); name.textContent = c.th;
    const all = document.createElement('button'); all.type = 'button'; all.textContent = `ดูทั้งหมด (${c.shapes.length})`;
    all.onclick = () => openCat(c.id);
    head.append(name, all);
    const row = document.createElement('div'); row.className = 'shapegrid';
    for (const s of c.shapes.slice(0, 4)) row.append(tile(s));
    sec.append(head, row);
    box.append(sec);
  }
  function openCat(id: string) {
    const c = SHAPE_CATS.find(x => x.id === id)!;
    $('elCatTitle').textContent = `${c.th} · ${c.shapes.length} แบบ`;
    const grid = $('elCatGrid');
    grid.textContent = '';
    for (const s of c.shapes) grid.append(tile(s));
    $('elMain').hidden = true; $('elCat').hidden = false;
    $('shElements').scrollTop = 0;
  }
  const back = () => { $('elCat').hidden = true; $('elMain').hidden = false; };
  $('elBack').onclick = back;

  /** put a shape on the page */
  function insert(id: string) {
    const why = h.cannotEdit();
    if (why) { toast(why, true); return; }
    const s = shapeById(id);
    if (!s) { toast('ไม่พบรูปทรงนี้', true); return; }
    const o = new fabric.Path(s.d, { fill: SHAPE_FILL, ...shapeBase, fillRule: s.evenOdd ? 'evenodd' : 'nonzero' } as fabric.IPathOptions) as Shape;
    const k = Math.min(ed.doc.w, ed.doc.h) * PLACE / Math.max(o.width ?? 1, o.height ?? 1, 1);
    o.set({ scaleX: k, scaleY: k, name: s.th.slice(0, 28) });
    o.shapeId = s.id; o.shapeKey = newKey();
    ed.place(o);
    if (!matchMedia('(min-width: 900px)').matches) closeSheets();
  }

  /** for the graphics search (องค์ประกอบ → กราฟิก): every shape, found by its Thai name and its category */
  const items = (): GfxItem[] => ALL_SHAPES.map(s => ({ src: 'shape', id: s.id, title: s.th, group: s.cat, hay: shapeWords(s), file: svgUrl(s, '#1d1a2b') }));

  /* ---------- the text in a shape ---------- */
  const textOf = (sh: Shape) => sh.shapeKey ? ed.userObjects().find(t => isThaiText(t) && (t as ShapeText).shapeLink === sh.shapeKey) as ShapeText | undefined : undefined;

  /** the text box fitted to its shape: centred, SHAPE_TEXT_WIDTH of its width */
  function fit(t: ShapeText, sh: Shape) {
    const w = sh.getScaledWidth() * SHAPE_TEXT_WIDTH / Math.abs(t.scaleX ?? 1);
    t.set('width', Math.max(20, w));
    t.initDimensions();
    t.setPositionByOrigin(sh.getCenterPoint(), 'center', 'center');
    t.setCoords();
  }

  /** type in a shape: its text (made the first time), chosen, ready to type */
  function editText(sh: Shape) {
    const why = h.cannotEdit();
    if (why) { toast(why, true); return; }
    if (sh.locked) { toast('รูปทรงนี้ล็อกอยู่ ปลดล็อกที่ "เลเยอร์" ก่อน', true); return; }
    if (!sh.shapeKey) sh.shapeKey = newKey();
    let t = textOf(sh);
    if (!t) {
      const size = Math.max(12, sh.getScaledHeight() * SHAPE_TEXT_SIZE);
      t = makeText('พิมพ์ข้อความ', { fontSize: size, width: sh.getScaledWidth() * SHAPE_TEXT_WIDTH, textAlign: 'center', fill: inkOn(sh.fill) }) as ShapeText;
      t.shapeLink = sh.shapeKey;
      sh.groupId ??= newGroupId();
      t.groupId = sh.groupId;
      fit(t, sh);
      const at = canvas.getObjects().indexOf(sh) + 1;
      const made = t;
      // one undo step (BUG-51): added quietly, recorded once
      history.silently(() => { canvas.add(made); canvas.moveTo(made, at); });
      canvas.fire('object:modified', { target: made });
    }
    canvas.setActiveObject(t);
    // in the same tap (iPad opens the keyboard only from a finger's own event)
    t.enterEditing();
    t.selectAll();
    canvas.requestRenderAll();
  }

  /* a double tap on a shape (mouse or finger; a shape in a group too) */
  let last: { key: string; at: number; x: number; y: number } | null = null;
  canvas.on('mouse:down', opt => {
    const e = opt.e as MouseEvent | TouchEvent;
    if ('touches' in e && e.touches.length > 1) { last = null; return; }
    const p = canvas.getPointer(e);
    // the topmost thing under the finger (page units: absolute corners); a text on the shape takes its own taps
    const at = new fabric.Point(p.x, p.y);
    const sh = [...ed.userObjects()].reverse().find(o => o.visible !== false && o.containsPoint(at, undefined, true)) as Shape | undefined;
    if (!sh?.shapeId) { last = null; return; }
    const now = Date.now(), key = sh.shapeKey ?? (sh.shapeKey = newKey());
    if (last && last.key === key && now - last.at < DOUBLE_MS && Math.hypot(p.x - last.x, p.y - last.y) * canvas.getZoom() < DOUBLE_PX) {
      last = null;
      editText(sh);
      return;
    }
    last = { key, at: now, x: p.x, y: p.y };
  });
  // the toolbar's chip "ข้อความ" (src/ui/toolbar.ts)
  canvas.on('shape:text' as never, ((opt: { target?: fabric.Object }) => { if (opt.target) editText(opt.target as Shape); }) as never);

  // the shape alone made bigger, smaller, turned or moved: its text follows (moved together, both move already)
  canvas.on('object:modified', opt => {
    const sh = opt.target as Shape | undefined;
    if (!sh?.shapeId) return;
    const t = textOf(sh);
    if (!t) return;
    t.set('angle', sh.angle ?? 0);
    fit(t, sh);
    canvas.requestRenderAll();
  });

  return { items, insert, back };
}
